import { useEffect, useState } from 'react';
import { Card, Drawer, Stat, PageHeader } from '@/components/ui';
import { Icon } from '@/components/ui/Icon';
import { Chart } from '@/components/ui/Chart';
import { superApi } from '@/shared/api/superApi';
import { toast } from '@/components/ui/Toast';

interface Report {
  id: string;
  he: string;
  description: string;
}

interface UsageReport {
  series: Array<{ label: string; value: number; calls: number }>;
  stats: { minutes_month: number; calls_month: number; agents_active: number };
  by_tenant: Array<{ tenant_id: number; tenant_name: string; calls: number; minutes: number }>;
}

interface RevenueReport {
  mrr_cents: number;
  arr_cents: number;
  arpu_cents: number;
  currency: string;
  active_tenants: number;
  overdue_invoices: number;
  overdue_cents: number;
  paid_last_30d_cents: number;
  by_plan: Array<{ plan_id: number; plan_name: string; price_cents: number; currency: string; tenant_count: number; subtotal_cents: number }>;
}

interface ChurnReport {
  window_days: number;
  churn_rate_pct: number;
  suspended_count: number;
  deleted_count: number;
  active_tenants: number;
  suspended: Array<{ id: number; name: string; slug: string; at: string }>;
  deleted: Array<{ id: number; name: string; slug: string; at: string }>;
}

interface NumbersReport {
  total: number;
  assigned: number;
  pool: number;
  utilization_pct: number;
  by_type: Array<{ type: string; count: number }>;
  top_tenants: Array<{ tenant_id: number; tenant_name: string; count: number }>;
}

interface QualityReport {
  window_days: number;
  total: number;
  answered: number;
  abandoned: number;
  failed: number;
  asr_pct: number;
  abandon_rate_pct: number;
  avg_wait_sec: number;
  avg_talk_sec: number;
}

export function SuperReports() {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState<Report | null>(null);

  useEffect(() => {
    void superApi
      .reports()
      .then((r) => setReports(r))
      .catch(() => toast('שגיאה בטעינת רשימת הדוחות', 'danger'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div style={{ padding: 'var(--gap)' }}>
        <Card>טוען…</Card>
      </div>
    );
  }

  return (
    <div className="stack" style={{ padding: 'var(--gap)' }}>
      <PageHeader title="דוחות מערכת" />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 'var(--gap)' }}>
        {reports.map((r) => (
          <Card key={r.id} title={r.he}>
            <p style={{ color: 'var(--text-2)', fontSize: 'var(--t-sm)', margin: '0 0 12px 0' }}>{r.description}</p>
            <button type="button" className="btn" onClick={() => setActive(r)}>הצג</button>
          </Card>
        ))}
      </div>

      <Drawer isOpen={!!active} onClose={() => setActive(null)} title={active?.he} width={720}>
        {active && <ReportBody report={active} />}
      </Drawer>
    </div>
  );
}

// Reports whose backend honors a ?days= window. revenue/numbers are point-in-time.
const WINDOWED = new Set(['usage', 'churn', 'quality']);
const WINDOW_OPTIONS = [7, 14, 30, 90];

function ReportBody({ report }: { report: Report }) {
  const [payload, setPayload] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [days, setDays] = useState(14);
  const windowed = WINDOWED.has(report.id);

  useEffect(() => {
    setLoading(true);
    setErr(null);
    void superApi
      .runReport(report.id, windowed ? { days } : undefined)
      .then((d) => setPayload(d))
      .catch(() => setErr('שגיאה בטעינת הדוח'))
      .finally(() => setLoading(false));
  }, [report.id, windowed, days]);

  return (
    <div className="stack">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        {windowed ? (
          <div style={{ display: 'flex', gap: 6 }}>
            {WINDOW_OPTIONS.map((d) => (
              <button
                key={d}
                type="button"
                className={`btn ${days === d ? 'primary' : ''}`}
                style={{ height: 30, padding: '0 10px' }}
                onClick={() => setDays(d)}
              >
                {d} ימים
              </button>
            ))}
          </div>
        ) : <span />}
        {!!payload && !err && (
          <button type="button" className="btn ghost" onClick={() => exportReportCsv(report, payload)} style={{ height: 30 }}>
            <Icon name="download" size={14} /> ייצוא CSV
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ color: 'var(--muted)' }}>טוען…</div>
      ) : err ? (
        <div style={{ color: 'var(--danger)' }}>{err}</div>
      ) : !payload ? (
        <div style={{ color: 'var(--muted)' }}>אין נתונים</div>
      ) : report.id === 'usage' ? <UsageView data={payload as UsageReport} />
        : report.id === 'revenue' ? <RevenueView data={payload as RevenueReport} />
        : report.id === 'churn' ? <ChurnView data={payload as ChurnReport} />
        : report.id === 'numbers' ? <NumbersView data={payload as NumbersReport} />
        : report.id === 'quality' ? <QualityView data={payload as QualityReport} />
        : null}
    </div>
  );
}

function UsageView({ data }: { data: UsageReport }) {
  return (
    <div className="stack">
      <Chart kind="bar" data={data.series.map((s) => ({ label: s.label, value: s.value }))} height={200} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--gap)' }}>
        <Stat label="דקות (חודש)" value={data.stats.minutes_month.toLocaleString('he-IL')} />
        <Stat label="נציגים פעילים" value={String(data.stats.agents_active)} />
        <Stat label="שיחות סה״כ" value={data.stats.calls_month.toLocaleString('he-IL')} />
      </div>

      <Section title="לפי לקוח (TOP 10 בחודש)">
        {data.by_tenant.length === 0 ? (
          <Muted>אין שיחות בחודש הנוכחי</Muted>
        ) : (
          <TableLite
            head={['לקוח', 'שיחות', 'דקות']}
            rows={data.by_tenant.map((t) => [t.tenant_name, t.calls.toLocaleString('he-IL'), t.minutes.toLocaleString('he-IL')])}
          />
        )}
      </Section>
    </div>
  );
}

function RevenueView({ data }: { data: RevenueReport }) {
  const fmt = (c: number) => new Intl.NumberFormat('he-IL', { style: 'currency', currency: data.currency }).format(c / 100);

  return (
    <div className="stack">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--gap)' }}>
        <Stat label="MRR" value={fmt(data.mrr_cents)} />
        <Stat label="ARR" value={fmt(data.arr_cents)} />
        <Stat label="חשבוניות באיחור" value={`${data.overdue_invoices} (${fmt(data.overdue_cents)})`} />
        <Stat label="ARPU" value={fmt(data.arpu_cents)} />
      </div>

      <Section title={`שולם ב-30 ימים אחרונים: ${fmt(data.paid_last_30d_cents)}`}>
        <Muted>{data.active_tenants} לקוחות פעילים</Muted>
      </Section>

      <Section title="לפי חבילה">
        {data.by_plan.length === 0 ? (
          <Muted>אין חבילות פעילות</Muted>
        ) : (
          <TableLite
            head={['חבילה', 'מחיר חודשי', 'לקוחות', 'סה״כ חודשי']}
            rows={data.by_plan.map((p) => [
              p.plan_name,
              new Intl.NumberFormat('he-IL', { style: 'currency', currency: p.currency }).format(p.price_cents / 100),
              String(p.tenant_count),
              new Intl.NumberFormat('he-IL', { style: 'currency', currency: p.currency }).format(p.subtotal_cents / 100),
            ])}
          />
        )}
      </Section>
    </div>
  );
}

function ChurnView({ data }: { data: ChurnReport }) {
  return (
    <div className="stack">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--gap)' }}>
        <Stat label={`Churn (${data.window_days} ימים)`} value={`${data.churn_rate_pct}%`} />
        <Stat label="הושעו" value={String(data.suspended_count)} />
        <Stat label="נמחקו" value={String(data.deleted_count)} />
      </div>

      <Section title="לקוחות שהושעו">
        {data.suspended.length === 0 ? (
          <Muted>אין</Muted>
        ) : (
          <TableLite
            head={['שם', 'מזהה', 'תאריך']}
            rows={data.suspended.map((t) => [t.name, t.slug, new Date(t.at).toLocaleDateString('he-IL')])}
          />
        )}
      </Section>

      <Section title="לקוחות שנמחקו">
        {data.deleted.length === 0 ? (
          <Muted>אין</Muted>
        ) : (
          <TableLite
            head={['שם', 'מזהה', 'תאריך']}
            rows={data.deleted.map((t) => [t.name, t.slug, new Date(t.at).toLocaleDateString('he-IL')])}
          />
        )}
      </Section>
    </div>
  );
}

function NumbersView({ data }: { data: NumbersReport }) {
  return (
    <div className="stack">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--gap)' }}>
        <Stat label="סה״כ" value={String(data.total)} />
        <Stat label="מוקצים" value={String(data.assigned)} />
        <Stat label="זמינים" value={String(data.pool)} />
        <Stat label="ניצול" value={`${data.utilization_pct}%`} />
      </div>

      <Section title="לפי סוג">
        {data.by_type.length === 0 ? (
          <Muted>אין מספרים</Muted>
        ) : (
          <TableLite
            head={['סוג', 'כמות']}
            rows={data.by_type.map((t) => [t.type, String(t.count)])}
          />
        )}
      </Section>

      <Section title="TOP לקוחות לפי כמות מספרים">
        {data.top_tenants.length === 0 ? (
          <Muted>אין מספרים מוקצים</Muted>
        ) : (
          <TableLite
            head={['לקוח', 'מספרים']}
            rows={data.top_tenants.map((t) => [t.tenant_name, String(t.count)])}
          />
        )}
      </Section>
    </div>
  );
}

function QualityView({ data }: { data: QualityReport }) {
  const fmtDur = (sec: number) => {
    if (sec < 60) return `${sec}s`;
    return `${Math.floor(sec / 60)}m ${sec % 60}s`;
  };

  return (
    <div className="stack">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--gap)' }}>
        <Stat label="ASR (אחוז מענה)" value={`${data.asr_pct}%`} />
        <Stat label="אחוז נטישה" value={`${data.abandon_rate_pct}%`} />
        <Stat label="המתנה ממוצעת" value={fmtDur(data.avg_wait_sec)} />
        <Stat label="משך שיחה ממוצע" value={fmtDur(data.avg_talk_sec)} />
      </div>

      <Section title={`פירוט (${data.window_days} ימים)`}>
        <TableLite
          head={['שיחות סה״כ', 'נענו', 'ננטשו', 'נכשלו']}
          rows={[[
            data.total.toLocaleString('he-IL'),
            data.answered.toLocaleString('he-IL'),
            data.abandoned.toLocaleString('he-IL'),
            data.failed.toLocaleString('he-IL'),
          ]]}
        />
      </Section>
    </div>
  );
}

// Build a CSV (header + rows) from a report payload and trigger a download.
function exportReportCsv(report: Report, payload: unknown) {
  let head: string[] = [];
  let rows: Array<Array<string | number>> = [];

  if (report.id === 'usage') {
    const d = payload as UsageReport;
    head = ['לקוח', 'שיחות', 'דקות'];
    rows = d.by_tenant.map((t) => [t.tenant_name, t.calls, t.minutes]);
  } else if (report.id === 'revenue') {
    const d = payload as RevenueReport;
    head = ['חבילה', 'מחיר חודשי', 'לקוחות', 'סה״כ חודשי', 'מטבע'];
    rows = d.by_plan.map((p) => [p.plan_name, p.price_cents / 100, p.tenant_count, p.subtotal_cents / 100, p.currency]);
  } else if (report.id === 'churn') {
    const d = payload as ChurnReport;
    head = ['שם', 'מזהה', 'מצב', 'תאריך'];
    rows = [
      ...d.suspended.map((t) => [t.name, t.slug, 'הושעה', t.at]),
      ...d.deleted.map((t) => [t.name, t.slug, 'נמחק', t.at]),
    ];
  } else if (report.id === 'numbers') {
    const d = payload as NumbersReport;
    head = ['לקוח', 'מספרים'];
    rows = d.top_tenants.map((t) => [t.tenant_name, t.count]);
  } else if (report.id === 'quality') {
    const d = payload as QualityReport;
    head = ['שיחות סה״כ', 'נענו', 'ננטשו', 'נכשלו', 'ASR%', 'נטישה%', 'המתנה ממוצעת(שנ)', 'משך ממוצע(שנ)'];
    rows = [[d.total, d.answered, d.abandoned, d.failed, d.asr_pct, d.abandon_rate_pct, d.avg_wait_sec, d.avg_talk_sec]];
  }

  if (rows.length === 0) {
    toast('אין נתונים לייצוא', 'warn');
    return;
  }

  const esc = (v: string | number) => {
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  // BOM so Excel reads Hebrew (UTF-8) correctly.
  const csv = '﻿' + [head, ...rows].map((r) => r.map(esc).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `merkaziya-${report.id}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="stack" style={{ gap: 6 }}>
      <h4 style={{ margin: '8px 0 4px', fontSize: 'var(--t-sm)', color: 'var(--text-2)' }}>{title}</h4>
      {children}
    </div>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <div style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>{children}</div>;
}

function TableLite({ head, rows }: { head: string[]; rows: Array<Array<string | number>> }) {
  return (
    <div style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-2)', overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: 'var(--surface-2)' }}>
            {head.map((h) => (
              <th key={h} style={{ padding: '8px 12px', textAlign: 'start', fontSize: 'var(--t-xs)', color: 'var(--muted)', fontWeight: 600 }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ borderTop: '1px solid var(--line)' }}>
              {r.map((c, j) => (
                <td key={j} style={{ padding: '8px 12px', fontSize: 'var(--t-sm)' }}>
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
